import { IContainerType, IProduct } from "@/models";
import { ICartItemContainerType } from "@/pages/point-of-sale/models/cart-item.model";
import {
  ISaleAddModel,
  ISaleAddModelContainerType,
} from "@/pages/point-of-sale/models/sale-add.model";
import { findProduct } from "@/pages/point-of-sale/services/product.service";

/** Valores de `tipo_pvp` / API de ventas → nombre canónico del catálogo POS */
const API_NAME_TO_CATALOG: Record<string, string> = {
  UNIDAD2: "PVP2",
  UNIDAD3: "PVP3",
  UND: "UNIDAD",
};

export interface ContainerTypeHints {
  id?: number | null;
  name?: string | null;
  alias?: string | null;
}

export interface ResolveCartContainerResult {
  containerType: ICartItemContainerType;
  matchedCatalog: boolean;
}

function normalizeKey(value: string): string {
  return value.trim().toUpperCase();
}

/** Normaliza nombres del API/BD al nombre usado en catálogo POS */
export function normalizePresentationName(name: string | null | undefined): string {
  if (!name?.trim()) return "";
  const key = normalizeKey(name);
  return API_NAME_TO_CATALOG[key] ?? name.trim();
}

export function getProductContainerTypes(product: IProduct | undefined): IContainerType[] {
  return product?.containerTypes?.[0] ?? [];
}

function matchesHint(ct: IContainerType, normalizedName: string): boolean {
  const ctName = normalizeKey(ct.name ?? "");
  const ctAlias = normalizeKey(ct.alias ?? "");

  if (!normalizedName) return false;

  if (ctName === normalizedName || ctAlias === normalizedName) return true;
  return normalizeKey(normalizePresentationName(ct.name)) === normalizedName;
}

/**
 * Busca una presentación en el catálogo del producto.
 * Prioridad: nombre (y alias) → id del catálogo.
 */
export function findContainerTypeInProduct(
  product: IProduct | undefined,
  hints: ContainerTypeHints
): IContainerType | undefined {
  const types = getProductContainerTypes(product);
  if (types.length === 0) return undefined;

  const rawName = hints.name ?? hints.alias ?? "";
  const normalizedName = rawName ? normalizeKey(normalizePresentationName(rawName)) : "";

  if (normalizedName) {
    const byName = types.find((ct) => matchesHint(ct, normalizedName));
    if (byName) return byName;
  }

  if (hints.id != null && hints.id > 0) {
    const byId = types.find((ct) => ct.id === hints.id);
    if (byId) return byId;
  }

  return undefined;
}

export function toCartContainerType(
  fromCatalog: IContainerType,
  priceOverride?: number
): ICartItemContainerType {
  return {
    id: fromCatalog.id,
    name: fromCatalog.name,
    quantity: fromCatalog.quantity,
    price: priceOverride ?? fromCatalog.price,
    alias: fromCatalog.alias,
  };
}

/** Resuelve presentación para líneas de carrito / restauración */
export function resolveCartContainerType(
  product: IProduct | undefined,
  hints: ContainerTypeHints,
  fallback?: Partial<ICartItemContainerType>
): ResolveCartContainerResult {
  const fromCatalog = findContainerTypeInProduct(product, hints);

  if (fromCatalog) {
    return {
      containerType: toCartContainerType(fromCatalog, fallback?.price),
      matchedCatalog: true,
    };
  }

  const name =
    normalizePresentationName(hints.name ?? hints.alias) ||
    hints.name?.trim() ||
    "UNIDAD";

  return {
    containerType: {
      id: hints.id ?? fallback?.id ?? 0,
      name,
      quantity: fallback?.quantity ?? 1,
      price: fallback?.price ?? 0,
      alias: hints.alias?.trim() || name,
    },
    matchedCatalog: false,
  };
}

/** Resuelve `container_type` para payload de venta (name desde catálogo) */
export function resolveSaleAddContainerType(
  product: IProduct | undefined,
  hints: ContainerTypeHints
): ISaleAddModelContainerType {
  const { containerType } = resolveCartContainerType(product, hints);
  return {
    id: containerType.id,
    name: containerType.name,
  };
}

export function resolveSaleAddContainerTypeForProduct(
  products: IProduct[] | undefined,
  productId: number,
  hints: ContainerTypeHints
): ISaleAddModelContainerType {
  const product = findProduct(products ?? [], productId);
  return resolveSaleAddContainerType(product, hints);
}

/** Compara presentación de carrito con detalle de error de stock */
export function cartContainerMatchesHint(
  cartName: string,
  cartId: number,
  hint: ContainerTypeHints
): boolean {
  const hintName = hint.name ?? hint.alias;
  if (hintName?.trim()) {
    const cartNorm = normalizeKey(normalizePresentationName(cartName));
    return cartNorm === normalizeKey(normalizePresentationName(hintName));
  }

  if (hint.id != null && hint.id > 0) {
    return cartId === hint.id;
  }

  return true;
}

/** Asegura nombres de presentación del catálogo antes de POST /sales */
export function normalizeSaleAddPayloadContainerTypes(
  payload: ISaleAddModel,
  products: IProduct[] | undefined
): void {
  for (const item of payload.bill.items) {
    item.container_type = resolveSaleAddContainerTypeForProduct(
      products,
      item.product_id,
      {
        id: item.container_type.id,
        name: item.container_type.name,
      }
    );
  }
}
